'use client';

import { FC, Fragment } from 'react';

import { Container } from '@purpur/library';

import { BlockCta } from '@telialt/core-cms-content/src/components/BlockCta/BlockCta';
import { renderWithBlockVisibility } from '@telialt/core-cms-content/src/components/BlockVisibility/renderWithBlockVisibility';
import { BlogSection } from '@telialt/core-cms-content/src/components/BlogSection/BlogSection';
import { AuthorBadge } from '@telialt/core-cms-content/src/components/common/AuthorBadge/AuthorBadge';
import { TagBlock } from '@telialt/core-cms-content/src/components/TagBlock/TagBlock';
import { CONTENT_TYPE_TO_URL_SLUG_MAP } from '@telialt/core-cms-content/src/constants';
import { AllEntryTypes } from '@telialt/core-cms-content/src/models/Content';
import { isContentItemBlockCta, isContentItemBlogSection } from '@telialt/core-cms-content/src/utils/typeGuards';
import { withParams } from '@telialt/utils/src/helpers';

import {
    ArticleAuthors,
    ArticleContentColumn,
    ArticleMetaColumn,
    ArticleSummary,
    ArticleTopContent,
    ColumnsContainer,
    MetaWrapper,
} from '../ContentBlogArticlePage.styles';
import { BlogArticlePageData } from '../model';
import { ArticleCategoryAndDate } from './ArticleCategoryAndDate';
import { ArticleTableOfContents } from './ArticleTableOfContents';

type Props = {
    articleContent: BlogArticlePageData;
};

export const ArticleContent: FC<Props> = ({ articleContent }) => {
    const { summary, categories, effectiveDate, authors, links, tags, content } = articleContent;

    const getArticleContentItem = (block: AllEntryTypes) => {
        if (isContentItemBlockCta(block)) {
            return renderWithBlockVisibility(block, <BlockCta contentItem={block} />);
        }

        if (isContentItemBlogSection(block)) {
            return renderWithBlockVisibility(block, <BlogSection contentItem={block} />);
        }

        return null;
    };

    return (
        <Container>
            <ColumnsContainer>
                <ArticleContentColumn>
                    <ArticleTopContent>
                        <ArticleCategoryAndDate
                            $target="mobile"
                            categories={categories}
                            effectiveDate={effectiveDate}
                        />
                        <ArticleSummary>{summary}</ArticleSummary>
                        <ArticleTableOfContents $target="mobile" links={links} />
                    </ArticleTopContent>

                    {content.map((contentItem) => (
                        <Fragment key={contentItem.sys.id}>{getArticleContentItem(contentItem)}</Fragment>
                    ))}
                </ArticleContentColumn>
                <ArticleMetaColumn>
                    <MetaWrapper>
                        <ArticleCategoryAndDate
                            $target="desktop"
                            categories={categories}
                            effectiveDate={effectiveDate}
                        />
                        <ArticleAuthors>
                            {authors.map((author, index) => (
                                <AuthorBadge
                                    key={author.id + index}
                                    image={author.image}
                                    description={author.description}
                                    name={author.title}
                                    url={withParams(CONTENT_TYPE_TO_URL_SLUG_MAP.blogAuthor, { slug: author.slug })}
                                />
                            ))}
                        </ArticleAuthors>
                    </MetaWrapper>
                    {tags.length > 0 && <TagBlock tags={tags} data-section-name="Article Tags" />}
                    <ArticleTableOfContents $target="desktop" links={links} />
                </ArticleMetaColumn>
            </ColumnsContainer>
        </Container>
    );
};
